/**
 * A knob's arithmetic. It turns with linear action, not by following the
 * pointer round its face: travel up (or right) raises the value, down lowers
 * it, a whole range per KNOB_TRAVEL_PX, landing on the knob's own steps and
 * stopping at either end. Pure; the knob itself is in rail.ts.
 */

export interface KnobRange {
  readonly min: number
  readonly max: number
  readonly step: number
}

/** The pointer travel that sweeps a knob from one end to the other. */
export const KNOB_TRAVEL_PX = 200

const decimals = (step: number): number => (String(step).split(".")[1] ?? "").length

/** The value a knob turned from `from` by `travel` px up shows. */
export const turned = (range: KnobRange, from: number, travel: number): number => {
  const raw = from + (travel / KNOB_TRAVEL_PX) * (range.max - range.min)
  const stepped = Math.round((raw - range.min) / range.step) * range.step + range.min
  const clamped = Math.min(range.max, Math.max(range.min, stepped))
  return Number(clamped.toFixed(decimals(range.step)))
}

/** How far round its sweep a knob showing `value` is, 0 to 1. */
export const sweep = (range: KnobRange, value: number): number =>
  Math.min(1, Math.max(0, (value - range.min) / (range.max - range.min)))
